import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError } from '../api/client';
import { Toast } from '../components/Overlay';

interface ToastState {
  /** Muestra un mensaje breve (ya traducido). */
  show: (message: string) => void;
  /** Muestra un error de la API traducido por su código (errors.<code>). */
  showError: (error: unknown) => void;
}

const ToastContext = createContext<ToastState | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((msg: string) => {
    setMessage(msg);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 2600);
  }, []);

  // Al desmontar se cancela el temporizador pendiente: si no, podía dispararse después de que la vista
  // ya no existe (en las pruebas, tras desmontar el entorno) y dejar un setState huérfano.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiRequestError) {
        const key = `errors.${error.code}`;
        show(t(key, { ...error.params, defaultValue: t('errors.UNKNOWN', { code: error.code }) }));
      } else {
        show(t('errors.UNKNOWN', { code: 'CLIENT' }));
      }
    },
    [show, t],
  );

  const value = useMemo(() => ({ show, showError }), [show, showError]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast message={message} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastState {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast requiere ToastProvider');
  return ctx;
}
